import type { HikingDifficulty, HikingMetadata, RouteType, UpdatePlaceData } from "~/lib/api";
import { DifficultySelector } from "~/components/place/DifficultySelector";
import { DistanceEditor } from "~/components/place/DistanceEditor";
import { ElevationEditor } from "~/components/place/ElevationEditor";
import { InfoField } from "~/components/place/InfoField";
import { RouteTypeSelector } from "./RouteTypeSelector";
import { TrackUploader } from "~/components/place/TrackUploader";
import { TrailStats } from "./TrailStats";
import { PanelSection } from "~/components/ui/panel-section";

interface TrailInfoSectionProps {
  metadata: HikingMetadata | null;
  canEdit?: boolean;
  onUpdate?: (data: UpdatePlaceData) => Promise<void>;
  track?: GeoJSON.LineString | null;
  onTrackUpload?: (file: File) => Promise<void>;
  onTrackDelete?: () => Promise<void>;
}

export function TrailInfoSection({
  metadata,
  canEdit,
  onUpdate,
  track,
  onTrackUpload,
  onTrackDelete,
}: TrailInfoSectionProps) {
  const hasContent = metadata?.difficulty || metadata?.distance || metadata?.elevation || metadata?.routeType || track;

  if (!canEdit && !hasContent) return null;

  const updateMetadata = (updates: Partial<HikingMetadata>) => {
    onUpdate?.({ metadata: { ...metadata, ...updates } });
  };

  return (
    <>
      <PanelSection.Separator />
      <PanelSection.Root>
        <PanelSection.Title>Trail Info</PanelSection.Title>
        <PanelSection.Content className="space-y-3">
          {canEdit ? (
            <>
              {onTrackUpload && <TrackUploader track={track} onUpload={onTrackUpload} onDelete={onTrackDelete} />}
              <InfoField label="Difficulty">
                <DifficultySelector
                  value={(metadata?.difficulty as HikingDifficulty) ?? null}
                  onChange={(difficulty) => updateMetadata({ difficulty: difficulty ?? undefined })}
                />
              </InfoField>
              <InfoField label="Route type">
                <RouteTypeSelector
                  value={(metadata?.routeType as RouteType) ?? null}
                  onChange={(routeType) => updateMetadata({ routeType: routeType ?? undefined })}
                />
              </InfoField>
              <InfoField label="Distance">
                <DistanceEditor
                  value={metadata?.distance}
                  onChange={(distance) => updateMetadata({ distance: distance ?? undefined })}
                  canEdit
                />
              </InfoField>
              <InfoField label="Elevation">
                <ElevationEditor
                  value={metadata?.elevation}
                  onChange={(elevation) => updateMetadata({ elevation: elevation ?? undefined })}
                  canEdit
                />
              </InfoField>
            </>
          ) : (
            <TrailStats metadata={metadata} className="text-muted-foreground" />
          )}
        </PanelSection.Content>
      </PanelSection.Root>
    </>
  );
}
